/**
 * The build stage belongs to the authoring agent, so the developer's page here is a
 * window, not a form. It is deliberately a summary: the budget, the rollouts and the
 * transcript live on the Agents board, which answers the same questions across every
 * task. What stays here is what is specific to *this* task -- where the agent got to,
 * what it published, and the one action a developer has, which is starting a job.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api/client'
import { Icon } from '@/components/Icon'
import { Picker } from '@/components/Picker'
import { RefLink, isLive, jobState } from '@/components/agent/job'
import type { Job } from '@/components/agent/types'
import { fmtDateTime } from '@/lib/format'
import type { Task } from '@/lib/types'

type View = { jobs: Job[]; can_start: boolean }

function JobRow({ taskId, job, jobs, selection, onSelect }: {
  taskId: string; job: Job; jobs: Job[]; selection: string; onSelect: (value: string) => void
}) {
  const state = jobState(job.status, job)
  const live = isLive(job.status)
  return <article className="agent-job">
    <div className="agent-job-selection">
      <div className="eyebrow">Authoring job</div>
      {jobs.length > 1 && <Picker label="Authoring job" value={selection} onChange={onSelect}
        options={jobs.map((item, index) => ({
          value: index === 0 ? '' : item.job_id,
          label: index === 0 ? 'Current job' : `Job ${jobs.length - index}`,
          detail: `${fmtDateTime(item.created_at)} · ${jobState(item.status, item).label}`,
        }))} />}
    </div>
    <header className="agent-job-head">
      <div>
        <h3>{state.label}{live && <span className="live-badge"><i className="live-dot" aria-hidden="true" />live</span>}</h3>
        <p className="muted small"><code>{job.job_id}</code> · started {fmtDateTime(job.created_at)}{job.finished_at ? ` · ended ${fmtDateTime(job.finished_at)}` : ''}</p>
      </div>
      <div className="agent-job-links">
        <RefLink value={job.candidate} label="Published candidate" />
        <RefLink value={job.final} label="Submitted package" />
        <Link className="rail-link" to={`/eng/agents/${taskId}/${job.job_id}`}>
          Rollouts and transcript<Icon name="arrow" size={15} />
        </Link>
      </div>
    </header>
    {job.blocker && <div className="note warn"><div className="who">The agent stopped and said why</div><div className="prewrap">{job.blocker}</div></div>}
  </article>
}

export function MonitorPanel({ task, onChanged, blocked = false }: { task: Task; onChanged?: () => void; blocked?: boolean }) {
  const changed = useRef(onChanged)
  changed.current = onChanged
  const lastState = useRef<string>()
  const [view, setView] = useState<View>({ jobs: [], can_start: false })
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // Empty selection follows the current job as polling discovers a newer build.
  const [selectedJob, setSelectedJob] = useState('')
  useEffect(() => {
    setSelectedJob('')
    setView({ jobs: [], can_start: false })
    lastState.current = undefined
    let stopped = false
    let timer: ReturnType<typeof setTimeout>
    async function poll() {
      try {
        const value = await api.get<View>(`/eng/tasks/${task.task_id}/authoring`)
        if (!stopped) {
          setView(value)
          const state = value.jobs[0] ? `${value.jobs[0].job_id}:${value.jobs[0].status}` : ''
          if (lastState.current !== undefined && state !== lastState.current) changed.current?.()
          lastState.current = state
        }
      }
      catch (e) { if (!stopped) setError(String(e)) }
      if (!stopped) timer = setTimeout(poll, 8000)
    }
    void poll()
    return () => { stopped = true; clearTimeout(timer) }
  }, [task.task_id])

  async function start() {
    if (blocked || busy) return
    setBusy(true); setError('')
    try {
      await api.post(`/eng/tasks/${task.task_id}/automation/runs`, {})
      setView(await api.get<View>(`/eng/tasks/${task.task_id}/authoring`))
      setSelectedJob('')
    }
    catch (e) { setError(String(e)) } finally { setBusy(false) }
  }

  const job = view.jobs.find(item => item.job_id === selectedJob) ?? view.jobs[0]
  const selection = job && job !== view.jobs[0] ? job.job_id : ''

  return <section className="agent-monitor">
    <div className="page-head">
      <div>
        <h2>The authoring agent</h2>
        <p className="muted">It builds the setup, exporter and verifier, runs its own rollouts, and opens the pull request that sends this task to quality check. This stage needs you only when it asks.</p>
      </div>
      {task.lifecycle !== 'retired' && task.authoring?.cancel_reason !== 'task_retired' && !(task.retirement?.stage === 3 && !task.authoring) && <button className="btn" disabled={!view.can_start || busy || blocked} onClick={start}>Start a new authoring job</button>}
    </div>
    {error && <div className="note err" role="alert">{error}</div>}
    {view.jobs.length === 0 && <p className="empty">No authoring job yet.</p>}
    {job && <JobRow taskId={task.task_id} job={job} jobs={view.jobs} selection={selection} onSelect={setSelectedJob} />}
  </section>
}
